import React from "react";
import InfiniteTable, { InfiniteTableProps } from "../Display/InfiniteTable";
import { API } from "aws-amplify";
import { IConnectListQuery } from "../../types";
import { Subtract } from "utility-types";

/**
 * This component is used to load data from a search query.
 * It is used to load data from a search query that returns a paginated list.
 * The search query is defined in the queryGenerator function.
 * The opName is the name of the operation in the response data.
 * Paging is handled by the from and limit variables in the queryGenerator function.
 * Has more data is determined by checking if the nextToken is present in the response data.
 */

type SearchConnectedInfiniteTableProps<T> = InfiniteTableProps<T> & {
  dataFetcher: (
    page: number | null
  ) => Promise<{ data: T[]; nextToken: string | null }>;
};

type InjectedProps<T> = {
  loadMore: () => Promise<void>;
  hasMoreData: boolean;
  loading: boolean;
  data: T[];
};

type State<T> = {
  nextToken: string | null;
  data: T[];
  loading: boolean;
  page: number | null;
};

class SearchConnectedInfiniteTable<RecordType> extends React.Component<
  Subtract<
    SearchConnectedInfiniteTableProps<RecordType>,
    InjectedProps<RecordType>
  >,
  State<RecordType>
> {
  state = {
    nextToken: null,
    loading: false,
    data: [],
    page: null,
  };

  tableDataSeed: string;

  changeDataSeed = (): void => {
    this.tableDataSeed = `${this.props.tableId}-dt-${Math.floor(Math.random() * 10000)}`;
  };

  componentDidMount(): void {
    if (this.props.tableId) {
      // Add listener for table refresh
      window.addEventListener(`refresh#${this.props.tableId}`, async () => {
        this.changeDataSeed();
        this.setState(
          {
            nextToken: null,
            page: null,
            data: [],
          },
          () => {
            this.loadMore();
          }
        );
      });
    }
  }
  loadMore = async (loadMoreRequestId?: number): Promise<void> => {
    this.setState({ loading: true });
    const page = (this.state.page || 0) + 1;
    if (!this.tableDataSeed) {
      this.changeDataSeed();
    }
    const seed = this.tableDataSeed;
    const response = await this.props.dataFetcher(page);
    if (seed !== this.tableDataSeed) {
      // Seed was changed while loading, we should ignore the data
      this.setState({ loading: false });
      return;
    }
    if (response.data) {
      this.setState((state: State<RecordType>) => ({
        data: [...state.data, ...response.data],
        nextToken: response.nextToken,
        loading: false,
        page,
      }));
      return;
    }
    this.setState({ loading: false });
  };
  render(): React.ReactElement {
    return (
      <InfiniteTable
        {...this.props}
        loadMore={this.loadMore}
        hasMoreData={this.state.nextToken !== null}
        loading={this.state.loading}
        data={this.state.data}
      />
    );
  }
}

export default SearchConnectedInfiniteTable;
